import { ARCHIVED_BANNER_HEIGHT } from "components/archived-project-banner";
import { useState, useMemo, useEffect, useRef, useContext } from "react";
import { AgGridReact } from "ag-grid-react"; // the AG Grid React Component
import { Button } from "antd";
import { EditOutlined } from "@ant-design/icons";
import { ColDef, GetRowIdFunc, GetRowIdParams } from "ag-grid-enterprise";
import {
  isPermissionNotGrantted,
  ProjectContext
} from "context/ProjectProvider";
import { GridLoadingIndicator } from "components/widgets";
import { useParams } from "react-router";
import {
  dlGrpUserNameCellRenderer,
  truncatedTextCellRenderer,
  TruncatedTextWithTooltip
} from "components/cell-renders";
import CiqLogPageHeader from "components/ciq-log-page-header";
import CiqAgSearch from "components/ciq-ag-search";
import { comparatorWithEmptyAtBottom } from "utils/grid-utils";
import { QUERY_DISTRIBUTION_GROUP_LIST } from "services/graphQL/queries";
import { useCIQQuery } from "hooks/ciq-gql-hooks";
import { ErrorMessages, ProjectPermissionEnum } from "../../constants";
import CreateDLGroup from "./create-distribution-group";

function resolveMemberCompany(projectUser: any): string {
  if (projectUser?.subscription) {
    const arr: any[] = projectUser.subscription.organization_subscriptions;
    return !arr || arr.length === 0 ? "" : arr[0].organization?.name;
  }
  if (projectUser?.subscription_vendor) {
    return projectUser?.subscription_vendor?.name;
  }
  return "";
}

/** A group with no members still gets one row so the grid can show the group. */
function emptyGroupRow(group: any) {
  const newuser: any = {};
  newuser.groupname = group?.name;
  newuser.grpid = group?.id;
  newuser.email = "";
  newuser.name = "";
  newuser.id = "";
  newuser.company = "";
  newuser.role = "";
  newuser.rowid = group?.id;
  return newuser;
}

function groupMemberRow(group: any, grpuser: any) {
  const newuser: any = {};
  newuser.groupname = group?.name;
  newuser.grpid = group?.id;
  newuser.email = grpuser?.user?.email;
  newuser.name = `${grpuser?.user?.first_name} ${grpuser?.user?.last_name}`;
  newuser.id = grpuser?.user?.id;
  newuser.role = "";
  newuser.company = "";
  newuser.rowid = `${group?.id}=${grpuser?.user?.id}`;

  if (grpuser?.user?.project_users?.length > 0) {
    const projectUser = grpuser?.user?.project_users[0];
    newuser.status_id = projectUser?.status_id;
    newuser.role = projectUser.project_role?.name;
    newuser.company = resolveMemberCompany(projectUser);
  }

  return newuser;
}

function toDistributionGroupRows(distributionGroups: Array<any>) {
  const grpusers: Array<any> = [];

  for (const group of distributionGroups) {
    if (group?.distribution_group_user?.length === 0) {
      grpusers.push(emptyGroupRow(group));
    }
    for (const grpuser of group.distribution_group_user) {
      if (grpuser) {
        grpusers.push(groupMemberRow(group, grpuser));
      }
    }
  }

  return grpusers;
}

function DistributionGroup(props: {
  gqlClientForProject: any;
  tokenContents: any;
  eventLogs: any[];
}) {
  const { gqlClientForProject, tokenContents, eventLogs } = props;
  const { isProjectArchived } = useContext(ProjectContext);
  const gridRef = useRef<AgGridReact>(null);
  const [isGridReady, setGridReady] = useState(false);
  const [open, setOpen] = useState(false);
  const [selectedGroupId, setSelectedGroupId] = useState<any>("");
  const { projectId } = useParams() as any;
  const [groupusers, setGroupData] = useState<any>();

  const { data: distributionGroupData, refetch: refetchDistributionGroupData } =
    useCIQQuery(QUERY_DISTRIBUTION_GROUP_LIST, {
      client: gqlClientForProject,
      variables: {
        where: {
          subscription: { project_users: { project_id: { _eq: projectId } } }
        }
      }
    });

  useEffect(() => {
    if (distributionGroupData) {
      setGroupData(
        toDistributionGroupRows(distributionGroupData.distribution_group)
      );
    }
  }, [distributionGroupData]);

  const previousEventLogs = useRef(eventLogs);
  useEffect(() => {
    if (eventLogs.length && previousEventLogs.current !== eventLogs) {
      if (
        eventLogs.some(
          (log) =>
            (log.data_source === "distribution_group" ||
              log.data_source === "distribution_user") &&
            (log.change_type === "update" ||
              log.change_type === "insert" ||
              log.change_type === "delete")
        )
      ) {
        refetchDistributionGroupData();
      }
    }
    previousEventLogs.current = eventLogs;
  }, [eventLogs, refetchDistributionGroupData]);

  const showModal = () => {
    setSelectedGroupId(null);
    setOpen(true);
  };

  const hideModal = () => {
    setSelectedGroupId(null);
    setOpen(false);
  };

  function editComponent(params: any) {
    if (!params?.node?.group) return "";
    return (
      <div className="pencil-icon" style={{ fontSize: "16px" }}>
        <EditOutlined />
      </div>
    );
  }

  function groupNameRenderer(params: any) {
    const { context, value } = params;

    if (context.groups.length === 0) return "";

    const group = context.groups.find((grp: any) => {
      return grp.id === value;
    });

    return <TruncatedTextWithTooltip text={group?.name || ""} />;
  }

  const gridContext = {
    groups: distributionGroupData?.distribution_group || []
  };

  // Each Column Definition results in one Column.
  const columnDefs = useMemo<ColDef[]>(() => {
    return [
      {
        headerName: "Distribution Group",
        headerTooltip: "Distribution Group",
        field: "grpid",
        colId: "grpid",
        cellRenderer: groupNameRenderer,
        rowGroup: true,
        hide: true,
        sort: "asc"
      },
      {
        headerName: "Name",
        headerTooltip: "Name",
        field: "name",
        colId: "name",
        cellRenderer: dlGrpUserNameCellRenderer
      },
      {
        headerName: "Email",
        headerTooltip: "Email",
        field: "email",
        colId: "email",
        cellRenderer: truncatedTextCellRenderer
      },
      {
        headerName: "Company",
        headerTooltip: "Company",
        field: "company",
        colId: "company",
        cellRenderer: truncatedTextCellRenderer
      },
      {
        headerName: "Role",
        headerTooltip: "Role",
        field: "role",
        colId: "role",
        cellRenderer: truncatedTextCellRenderer
      },
      {
        colId: "edit_pencil",
        headerName: "",
        resizable: true,
        maxWidth: 100,
        cellRenderer: editComponent,
        hide: isPermissionNotGrantted(
          ProjectPermissionEnum.EditDistributionGroup,
          tokenContents?.role ?? ""
        )
      }
    ];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [distributionGroupData]);

  const defaultColDef = useMemo<ColDef>(() => {
    return {
      sortable: true,
      autoHeight: true,
      resizable: true,
      headerClass: "header-style",
      comparator: comparatorWithEmptyAtBottom
    };
  }, []);

  const autoGroupColumnDef = useMemo<ColDef>(() => {
    return {
      headerName: "Distribution Group",
      headerTooltip: "DISTRIBUTION GROUP",
      tooltipValueGetter: (params) => {
        return params.value;
      },
      cellRendererParams: {
        suppressCount: true
      }
    };
  }, []);

  useEffect(() => {
    function handleResize() {
      if (isGridReady && gridRef && gridRef.current) {
        gridRef?.current?.api?.sizeColumnsToFit();
      }
    }
    window.addEventListener("resize", handleResize);
  }, [isGridReady]);

  const getRowId = useMemo<GetRowIdFunc>(() => {
    return (params: GetRowIdParams) => {
      return params?.data?.rowid || "";
    };
  }, []);

  const headerItems = [
    <CiqAgSearch key="search" gridRef={gridRef} placeholder="Search Groups" />,

    <Button
      key="create-btn"
      type="primary"
      onClick={showModal}
      title={
        isProjectArchived
          ? undefined
          : isPermissionNotGrantted(
                ProjectPermissionEnum.CreateDistributionGroup,
                tokenContents?.role ?? ""
              )
            ? ErrorMessages.PermissionNotGranted
            : "Create Distribution Group"
      }
      disabled={isPermissionNotGrantted(
        ProjectPermissionEnum.CreateDistributionGroup,
        tokenContents?.role ?? ""
      )}
      data-testid="create-distribution-group"
    >
      Create Distribution Group
    </Button>
  ];

  return (
    <div
      className="flex flex-col distribution-group"
      style={{ height: `calc(100vh - 144px - ${ARCHIVED_BANNER_HEIGHT})` }}
    >
      <CiqLogPageHeader
        entityName={
          <div className="flex flex-col space-y-1">
            <div className="text-xl font-semibold">Distribution Groups</div>
            <div className="text-sm font-normal text-ciq-gray-950">
              Create Distribution Groups required to be added across modules.
            </div>
          </div>
        }
        items={headerItems}
      />
      <div className="grow ag-theme-alpine">
        <AgGridReact<any>
          ref={gridRef}
          onGridReady={() => {
            setGridReady(true);
            gridRef?.current?.api?.sizeColumnsToFit();
          }}
          rowData={groupusers}
          columnDefs={columnDefs} // Column Defs for Columns
          defaultColDef={defaultColDef} // Default Column Properties
          autoGroupColumnDef={autoGroupColumnDef}
          animateRows={false} // Optional - set to 'true' to have rows animate when sorted
          loadingOverlayComponent={GridLoadingIndicator}
          suppressDragLeaveHidesColumns
          onCellClicked={(e) => {
            if (e.column.getColId() === "edit_pencil" && e.node.group) {
              setSelectedGroupId(e.data?.grpid || "");
              if (!e.data) {
                setSelectedGroupId(e.node?.key || "");
              }
              setOpen(true);
            }
          }}
          tooltipShowDelay={0}
          tooltipHideDelay={2000}
          tooltipShowMode="whenTruncated"
          getRowId={getRowId}
          context={gridContext}
        />
      </div>
      {open && (
        <CreateDLGroup
          selectedGroupId={selectedGroupId}
          setDrawerOpen={hideModal}
          showDrawerOpen={open}
          modelTitle={
            selectedGroupId
              ? "Edit Distribution Group"
              : "Create Distribution Group"
          }
        />
      )}
    </div>
  );
}

export default DistributionGroup;
